import React, { useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  FormNumberField,
  FormSelect,
  FormAutocomplete,
  FormPanel,
  FormContext,
  useFormSelector,
} from '@eas/common-web';
import {
  useRegions,
  usePrograms,
  useRuleSpecificTargets,
  useRuleActivities,
  useRuleNotices,
  useActiveEditors,
} from '../operator-api';
import { Rule } from '../../../common/models';

export function RulesFields() {
  const intl = useIntl();
  const { setFieldValue } = useContext(FormContext);
  const program =
    useFormSelector((data: Rule) => data.program) ?? undefined;
  const selectedSpecificTargets =
    useFormSelector((data: Rule) => data.specificTargets) ?? [];
  const selectedNotices =
    useFormSelector((data: Rule) => data.notices) ?? [];
  const regions = useRegions();
  const programs = usePrograms();
  const specificTargets = useRuleSpecificTargets(program);
  const deriveNoticesFromProgram =
    program != null && selectedSpecificTargets.length === 0;
  const noticeParents =
    selectedSpecificTargets.length > 0
      ? selectedSpecificTargets
      : deriveNoticesFromProgram
      ? specificTargets.items
      : [];
  const notices = useRuleNotices(
    noticeParents,
    deriveNoticesFromProgram &&
      (specificTargets.loading || noticeParents.length === 0)
  );
  const deriveActivitiesFromSpecificTargets =
    selectedNotices.length === 0 && selectedSpecificTargets.length > 0;
  const activityNotices =
    selectedNotices.length > 0
      ? selectedNotices
      : deriveActivitiesFromSpecificTargets
      ? notices.items
      : [];
  const activities = useRuleActivities(
    program,
    activityNotices,
    deriveActivitiesFromSpecificTargets &&
      (notices.loading || activityNotices.length === 0)
  );
  const operators = useActiveEditors();

  return (
    <FormPanel
      label={
        <FormattedMessage
          id="KS_O_RULES_FIELD_PANEL_TITLE"
          defaultMessage="Pravidlo"
        />
      }
    >
      <FormNumberField
        name="order"
        label={
          <FormattedMessage
            id="KS_O_RULES_FIELD_LABEL_ORDER"
            defaultMessage="Pořadí"
          />
        }
        helpLabel={intl.formatMessage({
          id: 'KS_O_RULES_FIELD_HELP_ORDER',
          defaultMessage: ' ',
        })}
      />
      <FormSelect
        name="regions"
        label={
          <FormattedMessage
            id="KS_O_RULES_FIELD_LABEL_REGIONS"
            defaultMessage="Kraje"
          />
        }
        helpLabel={intl.formatMessage({
          id: 'KS_O_RULES_FIELD_HELP_REGIONS',
          defaultMessage: ' ',
        })}
        source={regions}
        tooltipMapper={(o) => o.name}
        multiple={true}
      />
      <FormSelect
        name="program"
        label={
          <FormattedMessage
            id="KS_O_RULES_FIELD_LABEL_PROGRAM"
            defaultMessage="Program"
          />
        }
        helpLabel={intl.formatMessage({
          id: 'KS_O_RULES_FIELD_HELP_PROGRAM',
          defaultMessage: ' ',
        })}
        source={programs}
        tooltipMapper={(o) => o.name}
        notifyChange={() => {
          setFieldValue('specificTargets', undefined);
          setFieldValue('notices', undefined);
          setFieldValue('activities', undefined);
        }}
      />
      <FormSelect
        name="specificTargets"
        label={
          <FormattedMessage
            id="KS_O_RULES_FIELD_LABEL_SPECIFIC_TARGETS"
            defaultMessage="Specifické cíle"
          />
        }
        helpLabel={intl.formatMessage({
          id: 'KS_O_RULES_FIELD_HELP_SPECIFIC_TARGETS',
          defaultMessage: ' ',
        })}
        source={specificTargets}
        tooltipMapper={(o) => o.name}
        multiple={true}
        notifyChange={() => {
          setFieldValue('notices', undefined);
          setFieldValue('activities', undefined);
        }}
      />
      <FormSelect
        name="notices"
        label={
          <FormattedMessage
            id="KS_O_RULES_FIELD_LABEL_NOTICES"
            defaultMessage="Výzvy"
          />
        }
        helpLabel={intl.formatMessage({
          id: 'KS_O_RULES_FIELD_HELP_NOTICES',
          defaultMessage: ' ',
        })}
        source={notices}
        tooltipMapper={(o) => o.name}
        multiple={true}
        notifyChange={() => {
          setFieldValue('activities', undefined);
        }}
      />
      <FormSelect
        name="activities"
        label={
          <FormattedMessage
            id="KS_O_RULES_FIELD_LABEL_ACTIVITIES"
            defaultMessage="Aktivity"
          />
        }
        helpLabel={intl.formatMessage({
          id: 'KS_O_RULES_FIELD_HELP_ACTIVITIES',
          defaultMessage: ' ',
        })}
        source={activities}
        tooltipMapper={(o) => o.name}
        multiple={true}
      />
      <FormAutocomplete
        name="assignee"
        required
        label={
          <FormattedMessage
            id="KS_O_RULES_FIELD_LABEL_ASSIGNEE"
            defaultMessage="Řešitel"
          />
        }
        helpLabel={intl.formatMessage({
          id: 'KS_O_RULES_FIELD_HELP_ASSIGNEE',
          defaultMessage: ' ',
        })}
        source={operators}
        tooltipMapper={(o) => o.name}
      />
    </FormPanel>
  );
}
